import type { IntlShape } from 'react-intl';

import escapeRegExp from 'lodash/escapeRegExp';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { generatePath } from 'react-router';

import type { EntityType } from '~/features/managed-entities/entity-types/types/EntityType';

import { NavLink } from '~/eds/src/types/NavLink';
import { useEntityTypes } from '~/features/managed-entities/entity-types/hooks/useEntityTypes';
import { isGroupsSystemEntityType } from '~/features/managed-entities/entity-types/utils/isGroupsSystemEntityType';
import { isPartiesSystemEntityType } from '~/features/managed-entities/entity-types/utils/isPartiesSystemEntityType';
import { SavedSearch } from '~/features/search/types/search';
import { FlagType } from '~/flags/flags';
import { getFlag, useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useDashboardsSubnavs } from '~/hooks/useDashboardsSubnavs';
import { usePermissions } from '~/hooks/usePermissions';
import { canUseAutomation } from '~/permissions/canUseAutomation';
import { canUseManagedEntities } from '~/permissions/canUseManagedEntities';
import { canUseWorkflowAdmin } from '~/permissions/canUseWorkflowAdmin';
import { checkIsInternalSuperAdmin } from '~/permissions/checkIsInternalSuperAdmin';
import { checkIsWorkflowOnly } from '~/permissions/checkIsWorkflowOnly';
import { checkIsRbacAdmin } from '~/permissions/rbac/checkIsRbacAdmin';
import { DashboardAndPathname } from '~/redux/api/methods/dashboardV2';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { DashboardTab } from '~/types/Dashboard';
import { RbacPermission } from '~/types/Rbac';
import { User } from '~/types/User';

import { featureFlagIncluded } from './user';

const messages = defineMessages({
  workflowsClmEvolution: {
    id: 'PageHeader.navLinks.workflowsClmEvolution',
    defaultMessage: 'Workflows',
    description: 'Workflows CLM Evolution navigation link',
  },
  workflowsClmEvolutionNewBadge: {
    id: 'PageHeader.navLinks.workflowsClmEvolutionNewBadge',
    defaultMessage: 'New',
    description:
      'Badge shown on the Workflows CLM Evolution navigation link when a client also still has the legacy Workflow navigation link, to distinguish the two',
  },
  entityTypeGroups: {
    id: 'PageHeader.subNavLinks.entityTypeGroups',
    defaultMessage: 'Groups',
    description: 'Groups entity type Browse tab navigation link',
  },
  entityTypeParties: {
    id: 'PageHeader.subNavLinks.entityTypeParties',
    defaultMessage: 'Parties',
    description: 'Parties entity type Browse tab navigation link',
  },
});

// Both nav entries only coexist for clients transitioning off the legacy
// Workflow experience — net-new clients only ever see one, so there's nothing
// to disambiguate and the badge should stay hidden.
const hasBothWorkflowNavEntries = (
  currentUser: User,
  hasClmEvolutionPhase1?: boolean,
) =>
  !!hasClmEvolutionPhase1 && featureFlagIncluded(currentUser, 'WORKFLOW_DEMO');

interface Permissions {
  documents?: boolean;
  entityGroups?: boolean;
  workflowTickets?: boolean;
  workflowBuilder?: boolean;
  clauseLibrary?: boolean;
  automation?: boolean;
  documentXRay?: boolean;
  documentsDashboard?: boolean;
  workflowsDashboard?: boolean;
  managedEntityParties?: boolean;
}

// Guided AI / Quick AI clause tagging was hidden under Unified Fields M1
// and is back for M2. Keep the hide for M1-only clients; M2 implies M1,
// so the gate must carve M2 out.
const isClauseTaggingHiddenUnderUf = () =>
  getFlag(FlagType.UnifiedFieldsM1) && !getFlag(FlagType.UnifiedFieldsM2);

// Alerts were hidden under Unified Fields M1 and are back for M2. Keep the
// hide for M1-only clients; M2 implies M1, so the gate must carve M2 out.
const isAlertsHiddenUnderUf = () =>
  getFlag(FlagType.UnifiedFieldsM1) && !getFlag(FlagType.UnifiedFieldsM2);

const resolveDocumentsFallbackPathname = (entityTypes: EntityType[]) => {
  const groupsEntityType = entityTypes.find(isGroupsSystemEntityType);
  return groupsEntityType
    ? generatePath(RoutePathType.DocumentsEntityType, {
        entityTypeId: groupsEntityType.id,
      })
    : RoutePathType.Documents;
};

const buildEntityTypeActivePattern = (entityTypeId: string) =>
  `^/documents/entity-types/${escapeRegExp(entityTypeId)}(/|$)`;

// Full permission set every nav builder (top nav + sidebar) draws from.
export const NAV_PERMISSIONS: RbacPermission[] = [
  { resourceId: 'documents', resourceType: 'view' },
  { resourceId: 'workflow_tickets', resourceType: 'view' },
  { resourceId: 'workflow_templates', resourceType: 'view' },
  { resourceId: 'clause_library', resourceType: 'view' },
  { resourceId: 'clause_samples', resourceType: 'view' },
  { resourceId: 'document_x_ray', resourceType: 'view' },
  { resourceId: 'documents_dashboard', resourceType: 'view' },
  { resourceId: 'workflows_dashboard', resourceType: 'view' },
  { resourceId: 'entity_groups', resourceType: 'view' },
  { resourceId: 'entities', resourceType: 'view' },
  { resourceId: 'entity_types', resourceType: 'view' },
  { resourceId: 'conversational_ai', resourceType: 'edit' },
];

// Flag/permission inputs shared by useNavlinks and useSidebarNavlinks.
// `checkPermission`/`isLoading` are returned too since callers also need
// them for non-nav UI (profile menu, Ask AI gating, page loading state).
function useNavDependencies() {
  const { location } = useRouting();
  const { dashboards } = useDashboardsSubnavs();

  const hasClauseLibrary = useFlag(FlagType.ClauseLibrary);
  const enableFieldAi = useFlag(FlagType.FieldAi);
  const hasPlaybooks = useFlag(FlagType.Playbooks);
  const hasClmEvolutionPhase1 = useFlag(FlagType.CLMEvolutionPhase1);
  const hasDynamicDataExtraction = useFlag(FlagType.DynamicDataExtraction);
  const hasHome = useFlag(FlagType.Home);
  const hasMesPartiesMvp = useFlag(FlagType.MesPartiesMvp);

  const { checkPermission, isLoading: isPermissionsLoading } = usePermissions({
    permissions: NAV_PERMISSIONS,
  });

  const canAccessEntityGroups = hasMesPartiesMvp
    ? checkPermission('entities', 'view') ||
      checkPermission('entity_types', 'view')
    : checkPermission('entity_groups', 'view');

  // Without permission the fetch 403s on every render (EKP-58140).
  const { entityTypes } = useEntityTypes({ skip: !canAccessEntityGroups });

  const dashboard = useSelector(
    slices.dashboardV2.selectors.selectActiveDashboard,
  );
  const activeSearch = useSelector(slices.searchV3.selectors.selectSavedSearch);

  const permissions: Permissions = {
    documents: checkPermission('documents', 'view'),
    entityGroups: canAccessEntityGroups,
    workflowTickets: checkPermission('workflow_tickets', 'view'),
    workflowBuilder: checkPermission('workflow_templates', 'view'),
    clauseLibrary: checkPermission('clause_library', 'view'),
    automation: checkPermission('clause_samples', 'view'),
    // IMPORTANT to gate behind feature flag as we continue to refactor
    // formally for RBAC permissions.
    documentXRay: enableFieldAi && checkPermission('document_x_ray', 'view'),
    documentsDashboard: checkPermission('documents_dashboard', 'view'),
    workflowsDashboard: checkPermission('workflows_dashboard', 'view'),
    // TODO: add RBAC permissions.
    managedEntityParties: true,
  };

  return {
    permissions,
    entityTypes,
    activePathname: location.pathname,
    dashboards,
    activeSearch,
    activeDashboard: { ...dashboard, pathname: location.pathname },
    hasClauseLibrary,
    hasPlaybooks,
    hasClmEvolutionPhase1,
    hasDynamicDataExtraction,
    hasHome,
    isPermissionsLoading,
    checkPermission,
  };
}

export const useNavlinks = (onNavLinkClick: (pathname: string) => void) => {
  const currentUser = useCurrentUser();
  const intl = useIntl();
  const {
    dashboards,
    activeSearch,
    hasClauseLibrary,
    hasPlaybooks,
    activeDashboard,
    hasClmEvolutionPhase1,
    hasDynamicDataExtraction,
    hasHome,
    permissions,
    activePathname,
    entityTypes,
  } = useNavDependencies();

  const documentSubNavLinks = useDocumentSubNavLinks(
    currentUser,
    entityTypes,
    activePathname,
    {
      documents: permissions.documents,
      entityGroups: permissions.entityGroups,
    },
  );

  const navLinks: NavLink[] = [];
  if (checkIsWorkflowOnly(currentUser)) {
    if (featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
      return [
        {
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.workflowTickets',
            defaultMessage: 'Workflows',
            description: 'Workflow navigation link',
          }),
          pathname: RoutePathType.WorkflowTickets,
          children: [],
        },
      ];
    }
    return [];
  }

  if (hasHome) {
    navLinks.push({
      label: intl.formatMessage({
        id: 'PageHeader.navLinks.home',
        defaultMessage: 'Home',
        description: 'Home navigation link',
      }),
      pathname: RoutePathType.HomeSurface,
      children: [],
    });
  }

  if (!featureFlagIncluded(currentUser, 'REVIEWER')) {
    const shouldShowDocumentsNav = permissions['documents'];

    if (shouldShowDocumentsNav || permissions['entityGroups']) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.documents',
          defaultMessage: 'Documents',
          description: 'Documents navigation link',
        }),
        pathname: shouldShowDocumentsNav
          ? RoutePathType.Documents
          : resolveDocumentsFallbackPathname(entityTypes),
        children: documentSubNavLinks,
      });
    }

    // temporary, should not be a top level navlink
    // incorporate with Documents
    if (permissions['managedEntityParties']) {
      if (canUseManagedEntities(currentUser)) {
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.parties',
            defaultMessage: 'Parties',
            description: 'Parties navigation link',
          }),
          pathname: RoutePathType.ManagedEntities,
        });
      }
    }

    if (permissions['documents']) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.search',
          defaultMessage: 'Search',
          description: 'Search navigation link',
        }),
        pathname: RoutePathType.Search,
        children: createSearchSubNavLinks({
          activeSearch,
          onNavLinkClick,
          intl,
        }),

        onClick: () => onNavLinkClick(RoutePathType.Search),
      });
    }

    if (
      permissions['documentsDashboard'] ||
      permissions['workflowsDashboard']
    ) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.dashboards',
          defaultMessage: 'Dashboards',
          description: 'Dashboards navigation link',
        }),
        pathname: '/dashboards',
        children: createDashboardsSubNavLinks(
          currentUser,
          RoutePathType.Dashboards,
          intl,
          dashboards,
          onNavLinkClick,
          activeDashboard,
        ),
      });
    }

    const workflowPermissionCount = [
      permissions['workflowTickets'],
      permissions['workflowBuilder'],
      permissions['entityGroups'],
    ].filter(Boolean).length;

    if (permissions['workflowTickets'] || permissions['workflowBuilder']) {
      if (featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
        const shouldShowSubNav = workflowPermissionCount > 1;
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.workflow',
            defaultMessage: 'Workflows',
            description: 'Workflow navigation link',
          }),
          pathname: permissions['workflowTickets']
            ? RoutePathType.WorkflowTickets
            : RoutePathType.WorkflowBuilder,
          children: shouldShowSubNav
            ? createWorkflowSubNavLinks(
                currentUser,
                intl,
                permissions,
                entityTypes,
              )
            : [],
        });
      }
    }
    if (
      (permissions['workflowTickets'] || permissions['workflowBuilder']) &&
      hasClmEvolutionPhase1
    ) {
      const shouldShowSubNav = workflowPermissionCount > 1;
      const isClmEvolutionDetailPage =
        new RegExp(
          `^${RoutePathType.WorkflowCLMEvolutionWorkflows}/[^/]+`,
        ).test(activePathname) ||
        new RegExp(
          `^${RoutePathType.WorkflowCLMEvolutionRequestsDetail.replace(':ticketId', '[^/]+')}`,
        ).test(activePathname);
      const hasBothWorkflows = hasBothWorkflowNavEntries(
        currentUser,
        hasClmEvolutionPhase1,
      );
      navLinks.push({
        label: intl.formatMessage(messages.workflowsClmEvolution),
        pathname: permissions['workflowTickets']
          ? RoutePathType.WorkflowCLMEvolutionRequests
          : RoutePathType.WorkflowCLMEvolutionWorkflows,
        activePatterns: ['^/workflows/.+'],
        ...(hasBothWorkflows
          ? {
              badge: intl.formatMessage(messages.workflowsClmEvolutionNewBadge),
            }
          : {}),
        ...(shouldShowSubNav && !isClmEvolutionDetailPage
          ? {
              children: createWorkflowCLMEvolutionSubNavLinks(
                currentUser,
                intl,
                permissions,
              ),
            }
          : {}),
      });
    }

    if (permissions['clauseLibrary']) {
      if (hasClauseLibrary) {
        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.clauseLibrary',
            defaultMessage: 'Library',
            description: 'Clause Library navigation link',
          }),
          pathname: hasPlaybooks ? '/playbooks' : '/clause-library',
          children: hasPlaybooks ? createLibrarySubNavLinks(intl) : [],
          activePatterns: hasPlaybooks
            ? [
                '^/playbooks(/.*)?$',
                '^/guidelines(/.*)?$',
                '^/clause-library(/.*)?$',
              ]
            : ['^/clause-library(/.*)?$'],
        });
      }
    }

    if (permissions['documents']) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.upload',
          defaultMessage: 'Upload',
          description: 'Upload navigation link',
        }),
        pathname: RoutePathType.Upload,
        children: [],
      });
    }

    const hasAutomationPermission = permissions['automation'];
    const hasDocumentXRayPermission = permissions['documentXRay'];
    const hasWorkflowBuilderPermission = permissions['workflowBuilder'];
    const hasWorkflowRoutingAccess = hasWorkflowBuilderPermission;

    const hasAnyAutomationPermission =
      hasAutomationPermission ||
      hasDocumentXRayPermission ||
      hasWorkflowRoutingAccess;

    if (hasAnyAutomationPermission) {
      const canAccessViaAutomation = canUseAutomation(currentUser);
      const canAccessViaWorkflowBuilder =
        canUseWorkflowAdmin(currentUser) && hasWorkflowBuilderPermission;
      const canAccessAutomation =
        canAccessViaAutomation || canAccessViaWorkflowBuilder;

      // Guided AI / Quick AI clause tagging was hidden under Unified Fields
      // M1 and is back for M2 — mirrors the sidebar logic in buildSidebarCustomAiNavLink.
      const canAccessAutomationHub =
        hasAutomationPermission && !isClauseTaggingHiddenUnderUf();
      const canAccessAnyAutomationDestination =
        hasDocumentXRayPermission ||
        canAccessAutomationHub ||
        hasWorkflowRoutingAccess;

      if (canAccessAutomation && canAccessAnyAutomationDestination) {
        const getDefaultPathname = (): string => {
          if (hasDocumentXRayPermission) {
            return RoutePathType.AutomationHubFields;
          }
          if (hasWorkflowRoutingAccess && !canAccessAutomationHub) {
            return RoutePathType.RoutingTable;
          }
          return RoutePathType.AutomationHub;
        };

        const shouldShowSubNavLinks =
          ((hasAutomationPermission && hasDocumentXRayPermission) ||
            hasWorkflowRoutingAccess) &&
          shouldShowAutomationSubNavBar(activePathname);

        navLinks.push({
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.automation',
            defaultMessage: 'Automation',
            description: 'Automation navigation link',
          }),
          pathname: getDefaultPathname(),
          activePatterns: ['^/automation'],
          children: shouldShowSubNavLinks
            ? createAutomationSubNavLinks(
                permissions,
                intl,
                onNavLinkClick,
                hasDynamicDataExtraction,
              )
            : [],
        });
      }
    }

    if (
      checkIsInternalSuperAdmin(currentUser) ||
      featureFlagIncluded(currentUser, 'ADMIN') ||
      checkIsRbacAdmin(currentUser)
    ) {
      navLinks.push({
        label: intl.formatMessage({
          id: 'PageHeader.navLinks.adminConsole',
          defaultMessage: 'Admin',
          description: 'Admin Console navigation link',
        }),
        pathname: `/admin/console/${currentUser.client}`,
        children: [],
      });
    }
  }

  return navLinks;
};

const createWorkflowSubNavLinks = (
  currentUser: User,
  intl: IntlShape,
  permissions: Permissions,
  entityTypes: EntityType[],
) => {
  const isWorkflowAdmin =
    featureFlagIncluded(currentUser, 'WORKFLOW_DEMO') &&
    currentUser['is_workflow_admin'];

  const showTickets = isWorkflowAdmin || permissions['workflowTickets'];
  const links: NavLink[] = [];

  if (showTickets) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowTickets',
        defaultMessage: 'Tickets',
        description: 'Workflow Tickets navigation link',
      }),
      pathname: RoutePathType.WorkflowTickets,
      activePatterns: ['^/workflow/tickets/.+'],
    });
  }

  if (permissions.entityGroups) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowGroups',
        defaultMessage: 'Groups',
        description: 'Workflow Groups navigation link',
      }),
      pathname: resolveDocumentsFallbackPathname(entityTypes),
      neverActive: true,
    });
  }

  if (isWorkflowAdmin || permissions['workflowBuilder']) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowBuilder',
        defaultMessage: 'Workflow Builder',
        description: 'Workflow Builder navigation link',
      }),
      pathname: RoutePathType.WorkflowBuilder,
    });
  }

  return links;
};

const createWorkflowCLMEvolutionSubNavLinks = (
  currentUser: User,
  intl: IntlShape,
  permissions: Permissions,
) => {
  const isWorkflowAdmin =
    featureFlagIncluded(currentUser, 'WORKFLOW_DEMO') &&
    currentUser['is_workflow_admin'];

  const showTickets = isWorkflowAdmin || permissions['workflowTickets'];
  const links: NavLink[] = [];

  if (showTickets) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowRequests',
        defaultMessage: 'Requests',
        description: 'Workflow Requests navigation link',
      }),
      pathname: RoutePathType.WorkflowCLMEvolutionRequests,
      activePatterns: ['^/workflows/requests/.+'],
    });
  }

  if (isWorkflowAdmin || permissions['workflowBuilder']) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.workflowCLMEvolutionWorkflows',
        defaultMessage: 'Workflow Builder',
        description: 'Workflow CLM evolution workflow builder navigation link',
      }),
      pathname: RoutePathType.WorkflowCLMEvolutionWorkflows,
      activePatterns: ['^/workflows/workflow-builder/.+'],
    });
  }

  return links;
};

// Seeded system entity types ("Groups", "Parties") use fixed English names
// from the backend and need translating; custom type names are tenant-authored
// and must display verbatim.
const resolveEntityTypeLabel = (entityType: EntityType, intl: IntlShape) => {
  if (isGroupsSystemEntityType(entityType)) {
    return intl.formatMessage(messages.entityTypeGroups);
  }
  if (isPartiesSystemEntityType(entityType)) {
    return intl.formatMessage(messages.entityTypeParties);
  }
  return entityType.name;
};

// Builds one sub-nav (Browse) tab per managed entity type, including
// "Groups". `canAccessGroups` lets callers drop the Groups tab for users
// who can't view groups, since its route requires that permission.
const useEntityTypeSubNavLinks = (
  entityTypes: EntityType[],
  canAccessGroups = true,
): NavLink[] => {
  const intl = useIntl();
  const orderedEntityTypes = [
    ...entityTypes.filter((entityType) => entityType.is_system),
    ...entityTypes.filter((entityType) => !entityType.is_system),
  ];

  return orderedEntityTypes
    .filter(
      (entityType) => !isGroupsSystemEntityType(entityType) || canAccessGroups,
    )
    .map((entityType) => ({
      label: resolveEntityTypeLabel(entityType, intl),
      pathname: generatePath(RoutePathType.DocumentsEntityType, {
        entityTypeId: entityType.id,
      }),
      activePatterns: [buildEntityTypeActivePattern(entityType.id)],
    }));
};

export const useDocumentSubNavLinks = (
  currentUser: User,
  entityTypes: EntityType[] = [],
  activePathname = '',
  permissions: Pick<Permissions, 'documents' | 'entityGroups'>,
): NavLink[] => {
  const intl = useIntl();
  const entityTypeLinks = useEntityTypeSubNavLinks(
    entityTypes,
    !!permissions.entityGroups,
  );

  if (!shouldShowDocumentSubNavBar(currentUser, activePathname)) {
    return [];
  }

  const links: NavLink[] = [];

  if (permissions.documents) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.allDocuments',
        defaultMessage: 'All Documents',
        description: 'All Documents navigation link',
      }),
      pathname: RoutePathType.Documents,
      activePatterns: ['^/documents/\\d+'],
    });
  }

  // One Browse tab per entity type returned by the backend, including Groups.
  links.push(...entityTypeLinks);

  return links;
};

const createAutomationSubNavLink = (
  {
    label,
    pathname,
    activePatterns,
  }: {
    label: string;
    pathname: RoutePathType;
    activePatterns?: string[];
  },
  onNavLinkClick?: (pathname: string) => void,
): NavLink => ({
  label,
  pathname,
  activePatterns,
  onClick: () => onNavLinkClick?.(pathname),
});

export const createAutomationSubNavLinks = (
  permissions: Permissions,
  intl: IntlShape,
  onNavLinkClick?: (pathname: string) => void,
  hasDynamicDataExtraction = false,
) => {
  const subnavLinks: NavLink[] = [];
  if (permissions['documentXRay']) {
    subnavLinks.push(
      createAutomationSubNavLink(
        {
          label: intl.formatMessage({
            id: 'PageHeader.subNavLinks.fields',
            defaultMessage: 'Fields',
            description: 'Fields automation navigation link',
          }),
          pathname: RoutePathType.AutomationHubFields,
          activePatterns: ['^/automation/fields'],
        },
        onNavLinkClick,
      ),
    );
  }
  // Guided AI / Quick AI clause tagging was hidden under Unified Fields M1 and is back for M2.
  if (permissions['automation'] && !isClauseTaggingHiddenUnderUf()) {
    subnavLinks.push(
      createAutomationSubNavLink(
        {
          label: intl.formatMessage({
            id: 'PageHeader.subNavLinks.automation',
            defaultMessage: 'Clauses',
            description: 'Clause automation navigation link',
          }),
          pathname: RoutePathType.AutomationHub,
          activePatterns: ['^/automation$', '^/automation/detail'],
        },
        onNavLinkClick,
      ),
    );
  }
  if (permissions['workflowBuilder']) {
    subnavLinks.push(
      createAutomationSubNavLink(
        {
          label: intl.formatMessage({
            id: 'PageHeader.subNavLinks.routingTable',
            defaultMessage: 'Routing Table',
            description: 'Routing Table navigation link',
          }),
          pathname: RoutePathType.RoutingTable,
          activePatterns: ['^/automation/routing-table'],
        },
        onNavLinkClick,
      ),
    );
    if (hasDynamicDataExtraction) {
      subnavLinks.push(
        createAutomationSubNavLink(
          {
            label: intl.formatMessage({
              id: 'PageHeader.subNavLinks.dataExtraction',
              defaultMessage: 'Data Extraction',
              description: 'Data Extraction automation navigation link',
            }),
            pathname: RoutePathType.DynamicDataExtraction,
            activePatterns: ['^/automation/dynamic-data-extraction$'],
          },
          onNavLinkClick,
        ),
      );
    }
  }
  return subnavLinks;
};

export const createSearchSubNavLinks = ({
  intl,
  activeSearch,
  onNavLinkClick,
}: {
  intl: IntlShape;
  activeSearch?: SavedSearch;
  onNavLinkClick?: (pathname: string) => void;
}): NavLink[] => {
  const searchSubNavs: NavLink[] = [
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.search',
        defaultMessage: 'Search',
        description: 'Search navigation link',
      }),
      pathname: RoutePathType.Search,
      onClick: () => onNavLinkClick?.(RoutePathType.Search),
    },

    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.savedSearches',
        defaultMessage: 'Saved Searches',
        description: 'Saved Searches navigation link',
      }),
      pathname: RoutePathType.SavedSearches,
      onClick: () => onNavLinkClick?.(RoutePathType.SavedSearches),
    },
  ];

  if (activeSearch) {
    searchSubNavs.push({
      label: activeSearch.name,
      pathname: RoutePathType.SavedSearch.replace(
        ':searchId',
        `${activeSearch.id}`,
      ),
      onClick: () => {},
    });
  }

  if (!isAlertsHiddenUnderUf()) {
    searchSubNavs.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.alerts',
        defaultMessage: 'Alerts',
        description: 'Alerts navigation link',
      }),
      pathname: RoutePathType.Alerts,
    });
  }
  return searchSubNavs;
};

const createDashboardsSubNavLinks = (
  _currentUser: User,
  _pathname: string,
  intl: IntlShape,
  dashboards?: any[],
  onNavLinkClick?: (pathname: string) => void,
  activeDashboard?: DashboardAndPathname,
): NavLink[] => {
  const dashboardSubNavs = (dashboards || []).map((link: DashboardTab) => {
    const pathname = link.is_default
      ? `/dashboards/${link.id}`
      : `/dashboards/${link.type}`;

    const isActive = pathname === activeDashboard?.pathname;
    return {
      label: link.name,
      onClick: isActive ? undefined : () => onNavLinkClick?.(pathname),
      pathname,
    };
  });

  dashboardSubNavs.push({
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.savedDashboards',
      defaultMessage: 'Saved Dashboards',
      description: 'Saved Dashboards navigation link',
    }),
    onClick: () => onNavLinkClick?.(RoutePathType.SavedDashboards),
    pathname: RoutePathType.SavedDashboards,
  });

  if (
    activeDashboard &&
    activeDashboard.name &&
    shouldShowSavedDashboardLink(activeDashboard)
  ) {
    dashboardSubNavs.push({
      label: activeDashboard.name,
      pathname: activeDashboard.pathname,
      onClick: () => {},
    });
  }
  return dashboardSubNavs as NavLink[];
};

const lookerDashboardPagePattern = /(documents|expirations|tickets|workflows)/g;

const createLibrarySubNavLinks = (intl: IntlShape): NavLink[] => {
  return [
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.playbooks',
        defaultMessage: 'Playbooks',
        description: 'Playbooks navigation link',
      }),
      pathname: '/playbooks',
    },
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.aiInstructions',
        defaultMessage: 'Negotiation Requirements',
        description: 'Negotiation Requirements navigation link',
      }),
      pathname: '/guidelines',
    },
    {
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.clauses',
        defaultMessage: 'Clauses',
        description: 'Clauses navigation link',
      }),
      pathname: '/clause-library',
    },
  ];
};

const shouldShowSavedDashboardLink = (activeDashboard: DashboardAndPathname) =>
  activeDashboard?.pathname.includes('dashboards/') &&
  !activeDashboard?.pathname.match(lookerDashboardPagePattern) &&
  !activeDashboard.is_default;

export const shouldShowDocumentSubNavBar = (
  currentUser: User,
  pathname: string,
) =>
  !featureFlagIncluded(currentUser, 'WORKFLOW') &&
  pathname.startsWith(RoutePathType.Documents);

export const shouldShowAutomationSubNavBar = (pathname: string) =>
  !pathname.startsWith(
    RoutePathType.ViewDataExtraction.replace('/:extractionId', ''),
  );

const isSearchPathname = (pathname: string) =>
  pathname.startsWith(RoutePathType.Search) ||
  pathname.startsWith(RoutePathType.SavedSearches) ||
  pathname.startsWith(RoutePathType.DownloadExportReportPage);

export const shouldShowSearchSubNavbar = (pathname: string) =>
  isSearchPathname(pathname);

interface SidebarDocumentsSubNavLinksOptions {
  permissions: Permissions;
  entityTypes: EntityType[];
}

const useSidebarDocumentsSubNavLinks = ({
  permissions,
  entityTypes,
}: SidebarDocumentsSubNavLinksOptions): NavLink[] => {
  const intl = useIntl();
  // entityGroups is already flag-aware (EKP-59166), so this doesn't surface
  // a tab that dead-ends at the route gate.
  const canAccessGroups = !!permissions.entityGroups;
  const entityTypeLinks = useEntityTypeSubNavLinks(
    entityTypes,
    canAccessGroups,
  );

  const links: NavLink[] = [];

  if (permissions.documents) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.allDocuments',
        defaultMessage: 'All Documents',
        description: 'All Documents navigation link',
      }),
      pathname: RoutePathType.Documents,
      activePatterns: [String.raw`^/documents/\d+`],
    });
  }

  // One dynamic Browse tab per entity type (Groups reuses the groups route).
  links.push(...entityTypeLinks);

  return links;
};

const createSidebarLibrarySubNavLinks = (intl: IntlShape): NavLink[] => [
  {
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.playbooks',
      defaultMessage: 'Playbooks',
      description: 'Playbooks navigation link',
    }),
    pathname: '/playbooks',
  },
  {
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.aiInstructions',
      defaultMessage: 'Negotiation Requirements',
      description: 'Negotiation Requirements navigation link',
    }),
    pathname: '/guidelines',
  },
  {
    label: intl.formatMessage({
      id: 'PageHeader.subNavLinks.clauses',
      defaultMessage: 'Clauses',
      description: 'Clauses navigation link',
    }),
    pathname: '/clause-library',
  },
];

export const createCustomAiSubNavLinks = (
  permissions: Permissions,
  intl: IntlShape,
): NavLink[] => {
  const links: NavLink[] = [];

  if (permissions.documentXRay) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.fields',
        defaultMessage: 'Fields',
        description: 'Fields automation navigation link',
      }),
      pathname: RoutePathType.AutomationHubFields,
    });
  }

  // Guided AI / Quick AI clause tagging was hidden under Unified Fields M1 and is back for M2.
  if (permissions.automation && !isClauseTaggingHiddenUnderUf()) {
    links.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.automation',
        defaultMessage: 'Clauses',
        description: 'Clause automation navigation link',
      }),
      pathname: RoutePathType.AutomationHub,
    });
  }

  return links;
};

interface SidebarNavlinksOptions {
  currentUser: User;
  intl: IntlShape;
  onNavLinkClick: (pathname: string) => void;
  dashboards?: DashboardTab[];
  activeSearch?: SavedSearch;
  hasClauseLibrary?: boolean;
  hasPlaybooks?: boolean;
  activeDashboard?: DashboardAndPathname;
  hasClmEvolutionPhase1?: boolean;
  hasDynamicDataExtraction?: boolean;
  permissions?: Permissions;
  activePathname?: string;
  entityTypes?: EntityType[];
}

const resolveWorkflowNavPathname = ({
  hasClmEvolution,
  hasTicketsPermission,
}: {
  hasClmEvolution: boolean;
  hasTicketsPermission: boolean;
}) => {
  if (hasClmEvolution && hasTicketsPermission) {
    return RoutePathType.WorkflowCLMEvolutionRequests;
  }
  if (hasClmEvolution) {
    return RoutePathType.WorkflowCLMEvolutionWorkflows;
  }
  if (hasTicketsPermission) {
    return RoutePathType.WorkflowTickets;
  }
  return RoutePathType.WorkflowBuilder;
};

// Legacy "Workflow" — its own top-level row, separate from CLM Evolution's
// "Workflows" row below. Only shown for clients that still have the legacy
// experience; net-new CLM Evolution clients get only the row below.
const buildSidebarWorkflowNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const {
    currentUser,
    intl,
    permissions = {},
    entityTypes,
    hasDynamicDataExtraction,
  } = options;

  if (!permissions.workflowTickets && !permissions.workflowBuilder) {
    return null;
  }

  if (!featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
    return null;
  }

  const children = createWorkflowSubNavLinks(
    currentUser,
    intl,
    permissions,
    entityTypes ?? [],
  );

  // Sidebar-only: the legacy top nav keeps Routing Table (and Data
  // Extraction) under Automation (see createAutomationSubNavLinks / the
  // inline gating in useNavlinks).
  if (permissions.workflowBuilder) {
    children.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.routingTable',
        defaultMessage: 'Routing Table',
        description: 'Routing Table navigation link',
      }),
      pathname: RoutePathType.RoutingTable,
    });

    if (hasDynamicDataExtraction) {
      children.push({
        label: intl.formatMessage({
          id: 'PageHeader.subNavLinks.dataExtraction',
          defaultMessage: 'Data Extraction',
          description: 'Data Extraction automation navigation link',
        }),
        pathname: RoutePathType.DynamicDataExtraction,
      });
    }
  }

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.workflow',
      defaultMessage: 'Workflows',
      description: 'Workflow navigation link',
    }),
    pathname: permissions.workflowTickets
      ? RoutePathType.WorkflowTickets
      : RoutePathType.WorkflowBuilder,
    activePatterns: ['^/workflow/.+'],
    ...(children.length > 1 ? { children } : {}),
  };
};

// CLM Evolution — its own top-level "Workflows" row. Gets the "New" badge
// only while the legacy row above also renders, for clients transitioning
// between the two experiences.
const buildSidebarWorkflowCLMEvolutionNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const {
    currentUser,
    intl,
    hasClmEvolutionPhase1,
    hasDynamicDataExtraction,
    permissions = {},
  } = options;

  if (!permissions.workflowTickets && !permissions.workflowBuilder) {
    return null;
  }

  if (!hasClmEvolutionPhase1) {
    return null;
  }

  const hasBothWorkflowExperiences = featureFlagIncluded(
    currentUser,
    'WORKFLOW_DEMO',
  );

  const children = createWorkflowCLMEvolutionSubNavLinks(
    currentUser,
    intl,
    permissions,
  );

  // Sidebar-only: the legacy top nav keeps Routing Table (and Data
  // Extraction) under Automation (see createAutomationSubNavLinks / the
  // inline gating in useNavlinks).
  if (permissions.workflowBuilder) {
    children.push({
      label: intl.formatMessage({
        id: 'PageHeader.subNavLinks.routingTable',
        defaultMessage: 'Routing Table',
        description: 'Routing Table navigation link',
      }),
      pathname: RoutePathType.RoutingTable,
    });

    if (hasDynamicDataExtraction) {
      children.push({
        label: intl.formatMessage({
          id: 'PageHeader.subNavLinks.dataExtraction',
          defaultMessage: 'Data Extraction',
          description: 'Data Extraction automation navigation link',
        }),
        pathname: RoutePathType.DynamicDataExtraction,
      });
    }
  }

  return {
    label: intl.formatMessage(messages.workflowsClmEvolution),
    pathname: resolveWorkflowNavPathname({
      hasClmEvolution: true,
      hasTicketsPermission: !!permissions.workflowTickets,
    }),
    activePatterns: ['^/workflows/.+'],
    ...(hasBothWorkflowExperiences
      ? { badge: intl.formatMessage(messages.workflowsClmEvolutionNewBadge) }
      : {}),
    ...(children.length > 1 ? { children } : {}),
  };
};

const useBuildSidebarDocumentsNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { intl, permissions = {}, entityTypes } = options;

  const documentsChildren = useSidebarDocumentsSubNavLinks({
    permissions,
    entityTypes: entityTypes ?? [],
  });

  if (!permissions.documents && !permissions.entityGroups) return null;

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.documents',
      defaultMessage: 'Documents',
      description: 'Documents navigation link',
    }),
    pathname: permissions.documents
      ? RoutePathType.Documents
      : resolveDocumentsFallbackPathname(entityTypes ?? []),
    activePatterns: ['^/documents(/.*)?$'],
    children: documentsChildren,
  };
};

const buildSidebarDashboardsNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const {
    currentUser,
    intl,
    dashboards,
    onNavLinkClick,
    activeDashboard,
    permissions = {},
  } = options;

  if (!permissions.documentsDashboard && !permissions.workflowsDashboard) {
    return null;
  }

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.dashboards',
      defaultMessage: 'Dashboards',
      description: 'Dashboards navigation link',
    }),
    pathname: RoutePathType.Dashboards,
    activePatterns: ['^/dashboards(/.*)?$', '^/saved-dashboards(/.*)?$'],
    children: createDashboardsSubNavLinks(
      currentUser,
      RoutePathType.Dashboards,
      intl,
      dashboards,
      onNavLinkClick,
      activeDashboard,
    ),
  };
};

const buildSidebarSearchNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { intl, onNavLinkClick, activeSearch, permissions = {} } = options;

  if (!permissions.documents) return null;

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.search',
      defaultMessage: 'Search',
      description: 'Search navigation link',
    }),
    pathname: RoutePathType.Search,
    children: createSearchSubNavLinks({
      activeSearch,
      onNavLinkClick,
      intl,
    }),
    onClick: () => onNavLinkClick(RoutePathType.Search),
  };
};

const buildSidebarClauseLibraryNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { intl, hasClauseLibrary, hasPlaybooks, permissions = {} } = options;

  if (!permissions.clauseLibrary || !hasClauseLibrary) return null;

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.clauseLibrary',
      defaultMessage: 'Library',
      description: 'Clause Library navigation link',
    }),
    pathname: hasPlaybooks ? '/playbooks' : '/clause-library',
    children: hasPlaybooks ? createSidebarLibrarySubNavLinks(intl) : [],
    activePatterns: hasPlaybooks
      ? ['^/playbooks(/.*)?$', '^/guidelines(/.*)?$', '^/clause-library(/.*)?$']
      : ['^/clause-library(/.*)?$'],
  };
};

const buildSidebarUploadNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { intl, permissions = {} } = options;

  if (!permissions.documents) return null;

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.upload',
      defaultMessage: 'Upload',
      description: 'Upload navigation link',
    }),
    pathname: RoutePathType.Upload,
    children: [],
  };
};

const resolveCustomAiNavPathname = (hasDocumentXRayAccess: boolean) =>
  hasDocumentXRayAccess
    ? RoutePathType.AutomationHubFields
    : RoutePathType.AutomationHub;

const buildSidebarCustomAiNavLink = (
  options: SidebarNavlinksOptions,
): NavLink | null => {
  const { currentUser, intl, permissions = {} } = options;

  const hasAnyCustomAiPermission =
    permissions.automation || permissions.documentXRay;
  if (!hasAnyCustomAiPermission) return null;

  const canAccessCustomAi = canUseAutomation(currentUser);
  if (!canAccessCustomAi) return null;

  // Guided AI / Quick AI clause tagging was hidden under Unified Fields M1
  // and is back for M2, so the parent link can land on AutomationHub again when M2 is on.
  const canAccessAutomationHub =
    permissions.automation && !isClauseTaggingHiddenUnderUf();
  if (!permissions.documentXRay && !canAccessAutomationHub) {
    return null;
  }

  return {
    label: intl.formatMessage({
      id: 'SidebarNav.customAi',
      defaultMessage: 'Custom AI',
      description: 'Custom AI navigation link in the sidebar',
    }),
    pathname: resolveCustomAiNavPathname(!!permissions.documentXRay),
    activePatterns: ['^/automation(/.*)?$'],
    children: createCustomAiSubNavLinks(permissions, intl),
  };
};

export const buildSidebarAdminNavLink = (
  currentUser: User,
  intl: IntlShape,
): NavLink | null => {
  const isAdmin =
    checkIsInternalSuperAdmin(currentUser) ||
    featureFlagIncluded(currentUser, 'ADMIN') ||
    checkIsRbacAdmin(currentUser);

  if (!isAdmin) return null;

  return {
    label: intl.formatMessage({
      id: 'PageHeader.navLinks.adminConsole',
      defaultMessage: 'Admin',
      description: 'Admin Console navigation link',
    }),
    pathname: `/admin/console/${currentUser.client}`,
    children: [],
  };
};

export const useSidebarNavlinks = ({
  onNavLinkClick,
}: {
  onNavLinkClick: (pathname: string) => void;
}): NavLink[] => {
  const currentUser = useCurrentUser();
  const intl = useIntl();
  const deps = useNavDependencies();
  const options: SidebarNavlinksOptions = {
    ...deps,
    currentUser,
    intl,
    onNavLinkClick,
  };

  const documentsLink = useBuildSidebarDocumentsNavLink(options);

  if (checkIsWorkflowOnly(currentUser)) {
    if (featureFlagIncluded(currentUser, 'WORKFLOW_DEMO')) {
      return [
        {
          label: intl.formatMessage({
            id: 'PageHeader.navLinks.workflowTickets',
            defaultMessage: 'Workflows',
            description: 'Workflow navigation link',
          }),
          pathname: RoutePathType.WorkflowTickets,
          children: [],
        },
      ];
    }
    return [];
  }

  if (featureFlagIncluded(currentUser, 'REVIEWER')) {
    return [];
  }

  return [
    buildSidebarWorkflowNavLink(options),
    buildSidebarWorkflowCLMEvolutionNavLink(options),
    documentsLink,
    buildSidebarDashboardsNavLink(options),
    buildSidebarSearchNavLink(options),
    buildSidebarClauseLibraryNavLink(options),
    buildSidebarCustomAiNavLink(options),
    buildSidebarUploadNavLink(options),
  ].filter((link): link is NavLink => link !== null);
};
